import React, { FC } from 'react';

import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import PathwayNewIcon from '../../../../assets/icons/PathwayNewIcon';
import ReminderIcon from '../../../../assets/icons/ReminderIcon';
import ReminderNewIcon from '../../../../assets/icons/ReminderNewIcon';
import VideoNewIcon from '../../../../assets/icons/VideoNewIcon';
import styled from '../../../../styled';
import {
  colorsPalette,
  MIDDLE_RADIUS_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '../../../../theme';
import { NotificationType } from '../../../../types';

export enum AnnouncementType {
  Video = 'video',
  Pathway = 'pathway',
}

interface INotificationType {
  title: string;
  subtitle: string;
  highlighted?: boolean;
  type?: NotificationType | AnnouncementType;
  className?: string;
  link?: string;
}

const Notification: FC<INotificationType> = ({
  title,
  subtitle,
  highlighted,
  type,
  className,
  link,
}) => {
  const Icon = type ? iconsMap[type] : iconsMap[NotificationType.Reminder];
  return (
    <li className={className}>
      <Content as={link && Link} to={!!link && link} highlighted={highlighted}>
        <Icon secondaryColor="transparent" />
        <div>
          <Title as={link && Link} to={!!link && link}>
            {title}
          </Title>
          <Subtitle>{subtitle}</Subtitle>
        </div>
      </Content>
    </li>
  );
};

export default Notification;

const Content = styled('div', {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'as',
})`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: ${MIDDLE_RADIUS_PX};
  padding: ${TINY_MARGIN_PX} ${TINY_MARGIN_PX} ${TINY_MARGIN_PX} ${SMALL_MARGIN_PX};
  background: ${({ highlighted }) =>
    highlighted ? alpha(colorsPalette.blue, 0.33) : colorsPalette.violetAnalyticsCard};
  overflow: hidden;
  transition: 0.3s margin, 0.3s background-color;

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: ${TINY_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
    background: ${colorsPalette.violetAnalyticsCard};
  }

  &:hover {
    background: ${({ to }) => to && colorsPalette.violetAnalyticsToggle};
    margin-left: ${({ to }) => to && `-${TINY_MARGIN_PX}`};
    margin-right: ${({ to }) => to && `-${TINY_MARGIN_PX}`};
  }

  &::after {
    content: '';
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 8px;
    height: 100%;
    background: ${colorsPalette.blue};

    ${({ theme }) => theme.breakpoints.up('md')} {
      display: ${({ highlighted }) => highlighted && 'block'};
    }
  }
`;
const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'as',
})`
  display: block;
  margin-bottom: 4px;
  color: ${colorsPalette.white};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
`;
const Subtitle = styled('div')`
  font-size: 14px;
  color: ${colorsPalette.gray};
`;
const createStyledIcon = (tag) => styled(tag)`
  width: 24px;
  min-width: 24px;
  height: 24px;
  margin-right: ${SMALL_MARGIN_PX};
`;
const StyledPathwayNewIcon = createStyledIcon(PathwayNewIcon);
const StyledReminderNewIcon = createStyledIcon(ReminderNewIcon);
const StyledReminderIcon = createStyledIcon(ReminderIcon);
const StyledVideoNewIcon = createStyledIcon(VideoNewIcon);

const iconsMap = {
  [NotificationType.New]: StyledReminderNewIcon,
  [NotificationType.Reminder]: StyledReminderIcon,
  [AnnouncementType.Pathway]: StyledPathwayNewIcon,
  [AnnouncementType.Video]: StyledVideoNewIcon,
};
